import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { useTheme } from "@/theme/ThemeContext";
import {
  LogoutIcon,
  MoonIcon,
  PublishIcon,
  SearchIcon,
  SunIcon,
  UserCircleIcon,
  UsersIcon,
  flatPages,
} from "@/components/nav";

interface Command {
  id: string;
  label: string;
  group: string;
  icon: () => JSX.Element;
  run: () => void;
}

/** ⌘K / Ctrl-K overlay: type to jump to any page you're allowed to open, or to
 *  fire a quick action. Sources come from the shared nav model (see
 *  ``components/nav.tsx``), so a page added there is searchable for free — and
 *  a page a role can't open never appears.
 *
 *  Open/closed state is owned by Layout, which also owns the global key
 *  listener, so the shortcut works from any page. */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user, logout } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const nav = useNavigate();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const allowed = useMemo(() => new Set(user?.allowed_pages ?? []), [user]);

  const commands = useMemo<Command[]>(() => {
    const go = (to: string) => () => {
      onClose();
      nav(to);
    };
    const pages: Command[] = flatPages(allowed).map((p) => ({
      id: `page:${p.to}:${p.label}`,
      label: p.label,
      group: p.section,
      icon: p.icon,
      run: go(p.to),
    }));

    const actions: Command[] = [];
    if (allowed.has("/publish"))
      actions.push({
        id: "act:publish",
        label: "Publish a listing",
        group: "Actions",
        icon: PublishIcon,
        run: go("/publish"),
      });
    if (allowed.has("/market/publish"))
      actions.push({
        id: "act:bulk",
        label: "Start a bulk publish",
        group: "Actions",
        icon: PublishIcon,
        run: go("/market/publish"),
      });
    if (allowed.has("/clients"))
      actions.push({
        id: "act:clients",
        label: "Add a client",
        group: "Actions",
        icon: UsersIcon,
        run: go("/clients"),
      });
    actions.push(
      {
        id: "act:profile",
        label: "Open your profile",
        group: "Actions",
        icon: UserCircleIcon,
        run: go("/profile"),
      },
      {
        id: "act:theme",
        label: theme === "light" ? "Switch to dark theme" : "Switch to light theme",
        group: "Actions",
        icon: theme === "light" ? MoonIcon : SunIcon,
        run: () => {
          toggleTheme();
          onClose();
        },
      },
      {
        id: "act:logout",
        label: "Sign out",
        group: "Actions",
        icon: LogoutIcon,
        run: () => {
          onClose();
          logout();
        },
      },
    );

    return [...pages, ...actions];
  }, [allowed, theme, nav, onClose, toggleTheme, logout]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter(
      (c) =>
        c.label.toLowerCase().includes(q) || c.group.toLowerCase().includes(q),
    );
  }, [commands, query]);

  // Fresh query → selection back to the top, clamped so an empty result set
  // can't leave `active` pointing past the end.
  useEffect(() => {
    setActive(0);
  }, [query, open]);
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  // Body-scroll lock while open — same treatment as the filters drawer.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Keep the highlighted row in view while arrowing through a long list.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [active]);

  if (!open) return null;

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (results.length ? (i + 1) % results.length : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (results.length ? (i - 1 + results.length) % results.length : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      results[active]?.run();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  }

  // Group headings are rendered inline as the list is walked, so the flat
  // keyboard index stays simple (one counter across every group).
  let lastGroup = "";

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center px-4 pt-[14vh]">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onKeyDown={onKeyDown}
        className="relative w-full max-w-lg rounded-2xl bg-ink-900 border border-white/10 shadow-2xl shadow-black/40 overflow-hidden"
      >
        <div className="flex items-center gap-3 px-4 h-12 border-b border-white/5">
          <span className="text-slate-500">
            <SearchIcon />
          </span>
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search pages and actions…"
            className="flex-1 bg-transparent text-[14px] text-slate-100 placeholder:text-slate-600 focus:outline-none"
          />
          <kbd className="hidden sm:inline text-[10px] font-semibold text-slate-500 border border-white/10 rounded px-1.5 py-0.5">
            ESC
          </kbd>
        </div>

        <div ref={listRef} className="max-h-[min(24rem,56vh)] overflow-y-auto scrollbar-thin py-1.5">
          {results.length === 0 && (
            <div className="px-4 py-8 text-center text-[13px] text-slate-500">
              Nothing matches “{query}”.
            </div>
          )}
          {results.map((c, i) => {
            const heading = c.group !== lastGroup ? c.group : null;
            lastGroup = c.group;
            return (
              <div key={c.id}>
                {heading && (
                  <div className="px-4 pt-2.5 pb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-600">
                    {heading}
                  </div>
                )}
                <button
                  type="button"
                  data-idx={i}
                  onMouseEnter={() => setActive(i)}
                  onClick={c.run}
                  className={`w-full flex items-center gap-3 px-4 py-2 text-left transition ${
                    i === active
                      ? "bg-accent-500/10 text-slate-100"
                      : "text-slate-300 hover:bg-white/[0.03]"
                  }`}
                >
                  <span className={i === active ? "text-accent-300" : "text-slate-500"}>
                    <c.icon />
                  </span>
                  <span className="flex-1 text-[13.5px] truncate">{c.label}</span>
                  {i === active && (
                    <kbd className="text-[10px] font-semibold text-slate-500 border border-white/10 rounded px-1.5 py-0.5">
                      ↵
                    </kbd>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
